import { useParams, useNavigate } from 'react-router-dom';
import { useGetLibraryItemQuery, useGetConnectedAllQuery } from '../store/docApi';
import { Box, Typography, CircularProgress, Card, Button, Chip, Grid } from '@mui/material';
import ColorChartThumbnail from '../components/charts/ColorChartThumbnail';

export default function LibraryItemDetails() {
  const { label, pk, id } = useParams();
  const navigate = useNavigate();
  const { data: item, error, isLoading } = useGetLibraryItemQuery({ label, pk, id });
  const { data: projects } = useGetConnectedAllQuery({ _id: id, label: 'project' });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" textAlign="center" sx={{ mt: 4 }}>
        Failed to load library item details: {error.message}
      </Typography>
    );
  }

  if (!item) {
    return (
      <Typography textAlign="center" sx={{ mt: 4 }}>
        Library item not found.
      </Typography>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        {item.name}
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        {item.description}
      </Typography>

      {/* Item Picture */}
      {item.picture && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <img src={item.picture} alt={item.name} style={{ maxWidth: '100%', maxHeight: 400 }} />
        </Box>
      )}

      {item.motifs && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2, mb: 4 }}>
          {item.motifs.map((motif, i) => (
            <Card key={i} variant="outlined" sx={{ p: 2, textAlign: 'center', alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {motif.name}
              </Typography>
              {motif.picture && <img src={motif.picture} alt={motif.name} style={{ maxWidth: '100%', maxHeight: 150, marginBottom: 8 }} />}
              {motif.data && <ColorChartThumbnail data={motif.data} colors={motif.colors} />}
            </Card>
          ))}
        </Box>
      )}

      <Box sx={{ maxWidth: 600, mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            My Projects
          </Typography>
          <Button size="small" variant="outlined" onClick={() => navigate(`/projects/create/${item.id}`)}>
            New Project
          </Button>
        </Box>
        <Grid container spacing={2}>
          {projects &&
            projects.map((project, i) => (
              <Card
                key={i}
                variant="outlined"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 1 }
                }}
                onClick={() => navigate(`/projects/${project.pk}/${project.id}`)}
              >
                <Typography>{project.name}</Typography>
                <Chip size="small" variant="outlined" label="→" />
              </Card>
            ))}
        </Grid>
      </Box>
    </Box>
  );
}
